/**
 * CherishedLegacy.org — B2B Funeral Home Marketing Site
 * Design: Dark navy (#0d1b2a), gold (#c9a84c), cream (#f5f0e8)
 * Fonts: Playfair Display (headings) + DM Sans (body)
 * Sections: Hero, How It Works, Partnership Trust, Pricing, Workflow, Inquiry Form
 */

import React, { useState } from "react";
import { toast } from "sonner";

const HERO_BG = "https://d2xsxph8kpxj0f.cloudfront.net/92373216/m4pBdcEoqG5JfKLSbSWXhK/cl-hero-bg_5be65428.png";
const SEAL_LOGO = "https://d2xsxph8kpxj0f.cloudfront.net/92373216/m4pBdcEoqG5JfKLSbSWXhK/cl-seal-logo_33c40ad4.png";
const PARTNERSHIP_PHOTO = "https://d2xsxph8kpxj0f.cloudfront.net/92373216/m4pBdcEoqG5JfKLSbSWXhK/cl-partnership-photo_ff1f4a87.png";

const NAV_LINKS = [
  { label: "The Service", id: "service" },
  { label: "For Funeral Homes", id: "trust" },
  { label: "Service Tiers", id: "pricing" },
  { label: "Contact", id: "inquiry" },
];

function scrollTo(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
}

export default function CherishedLegacyHolding() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    funeralHome: "",
    email: "",
    phone: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);

  const [isPending, setIsPending] = React.useState(false);
  const notifyMutation = {
    isPending,
    mutate: async (payload: { title: string; content: string }) => {
      setIsPending(true);
      try {
        const res = await fetch("/api/notify", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error("Server error");
        setSubmitted(true);
        setForm({ name: "", funeralHome: "", email: "", phone: "", message: "" });
      } catch {
        toast.error("Submission failed. Please try again or email us directly.");
      } finally {
        setIsPending(false);
      }
    },
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.funeralHome || !form.email) {
      toast.error("Please fill in your name, funeral home, and email.");
      return;
    }
    notifyMutation.mutate({
      title: `Partnership Inquiry — ${form.funeralHome}`,
      content: `Name: ${form.name}\nFuneral Home: ${form.funeralHome}\nEmail: ${form.email}\nPhone: ${form.phone || "—"}\nMessage: ${form.message || "—"}`,
    });
  };

  const inputStyle: React.CSSProperties = {
    padding: "0.85rem 1rem",
    backgroundColor: "rgba(13,27,42,0.8)",
    border: "1px solid rgba(201,168,76,0.25)",
    borderRadius: 4,
    color: "#f5f0e8",
    fontSize: "0.9rem",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
    fontFamily: "'DM Sans', sans-serif",
  };

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", backgroundColor: "#0d1b2a", color: "#f5f0e8", minHeight: "100vh" }}>

      {/* ── NAV ── */}
      <nav style={{ position: "sticky", top: 0, zIndex: 50, backgroundColor: "rgba(13,27,42,0.96)", backdropFilter: "blur(8px)", borderBottom: "1px solid rgba(201,168,76,0.2)" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 1.5rem", display: "flex", alignItems: "center", justifyContent: "space-between", height: 64 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", cursor: "pointer" }} onClick={() => scrollTo("hero")}>
            <img src={SEAL_LOGO} alt="Cherished Legacy Seal" style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover" }} />
            <div>
              <div style={{ fontFamily: "'Playfair Display', serif", fontSize: "1rem", fontWeight: 700, color: "#c9a84c", lineHeight: 1.1 }}>CherishedLegacy</div>
              <div style={{ fontSize: "0.62rem", letterSpacing: "0.14em", textTransform: "uppercase", color: "#a09070", lineHeight: 1 }}>Memorial Registry</div>
            </div>
          </div>

          {/* Desktop nav */}
          <div className="hidden md:flex" style={{ gap: "2rem", alignItems: "center", display: "flex" }}>
            {NAV_LINKS.map(l => (
              <button key={l.id} onClick={() => scrollTo(l.id)}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#d4c9b0", fontSize: "0.85rem", letterSpacing: "0.04em", padding: 0, transition: "color 0.2s" }}
                onMouseEnter={e => (e.currentTarget.style.color = "#c9a84c")}
                onMouseLeave={e => (e.currentTarget.style.color = "#d4c9b0")}>
                {l.label}
              </button>
            ))}
            <button onClick={() => scrollTo("inquiry")}
              style={{ padding: "0.5rem 1.25rem", border: "1px solid #c9a84c", borderRadius: 4, background: "transparent", color: "#c9a84c", fontSize: "0.8rem", letterSpacing: "0.08em", cursor: "pointer", transition: "all 0.2s" }}
              onMouseEnter={e => { e.currentTarget.style.background = "#c9a84c"; e.currentTarget.style.color = "#0d1b2a"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#c9a84c"; }}>
              Partner With Us
            </button>
          </div>

          {/* Mobile hamburger */}
          <button onClick={() => setMenuOpen(!menuOpen)}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#c9a84c", fontSize: "1.5rem", lineHeight: 1 }}
            className="md:hidden">
            {menuOpen ? "✕" : "☰"}
          </button>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div style={{ backgroundColor: "#0d1b2a", borderTop: "1px solid rgba(201,168,76,0.15)", padding: "1rem 1.5rem", display: "flex", flexDirection: "column", gap: "1rem" }}>
            {NAV_LINKS.map(l => (
              <button key={l.id} onClick={() => { scrollTo(l.id); setMenuOpen(false); }}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#d4c9b0", fontSize: "0.9rem", textAlign: "left", padding: 0 }}>
                {l.label}
              </button>
            ))}
            <button onClick={() => { scrollTo("inquiry"); setMenuOpen(false); }}
              style={{ padding: "0.6rem 1rem", border: "1px solid #c9a84c", borderRadius: 4, background: "transparent", color: "#c9a84c", fontSize: "0.85rem", cursor: "pointer", textAlign: "left" }}>
              Partner With Us
            </button>
          </div>
        )}
      </nav>

      {/* ── HERO ── */}
      <section id="hero" style={{ position: "relative", minHeight: "90vh", display: "flex", alignItems: "center" }}>
        <div style={{ position: "absolute", inset: 0, backgroundImage: `url(${HERO_BG})`, backgroundSize: "cover", backgroundPosition: "center", filter: "brightness(0.32)" }} />
        <div style={{ position: "relative", zIndex: 1, maxWidth: 1200, margin: "0 auto", padding: "5rem 1.5rem", width: "100%" }}>
          <div style={{ maxWidth: 680 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", padding: "0.35rem 0.9rem", border: "1px solid rgba(201,168,76,0.4)", borderRadius: 2, marginBottom: "1.5rem" }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "#c9a84c", display: "inline-block", flexShrink: 0 }} />
              <span style={{ fontSize: "0.68rem", letterSpacing: "0.15em", textTransform: "uppercase", color: "#c9a84c" }}>A Service for Funeral Professionals</span>
            </div>

            <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2.2rem, 5vw, 3.8rem)", fontWeight: 700, lineHeight: 1.15, marginBottom: "1.5rem", color: "#f5f0e8" }}>
              Every Life Deserves<br />
              <span style={{ color: "#c9a84c" }}>a Permanent Record.</span>
            </h1>

            <p style={{ fontSize: "1.05rem", lineHeight: 1.75, color: "#c4b89a", maxWidth: 560, marginBottom: "2.5rem" }}>
              CherishedLegacy.org is a professional memorial video registry. We partner with funeral homes to provide families with a cinematic, archival-quality tribute — produced entirely by our team, delivered within 48 hours, with no production overhead on your staff.
            </p>

            <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
              <button onClick={() => scrollTo("inquiry")}
                style={{ padding: "0.85rem 2rem", background: "#c9a84c", color: "#0d1b2a", border: "none", borderRadius: 4, fontWeight: 700, fontSize: "0.9rem", letterSpacing: "0.05em", cursor: "pointer", transition: "opacity 0.2s" }}
                onMouseEnter={e => (e.currentTarget.style.opacity = "0.88")}
                onMouseLeave={e => (e.currentTarget.style.opacity = "1")}>
                Request Partnership Information
              </button>
              <button onClick={() => scrollTo("service")}
                style={{ padding: "0.85rem 2rem", background: "transparent", color: "#f5f0e8", border: "1px solid rgba(245,240,232,0.3)", borderRadius: 4, fontSize: "0.9rem", letterSpacing: "0.05em", cursor: "pointer", transition: "border-color 0.2s" }}
                onMouseEnter={e => (e.currentTarget.style.borderColor = "#c9a84c")}
                onMouseLeave={e => (e.currentTarget.style.borderColor = "rgba(245,240,232,0.3)")}>
                How It Works
              </button>
            </div>
          </div>

          {/* Stat badges */}
          <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap", marginTop: "3rem" }}>
            {[
              { value: "48 hrs", label: "Delivery Guarantee" },
              { value: "100%", label: "Human-Reviewed" },
              { value: "3 Tiers", label: "Wholesale Pricing" },
            ].map(s => (
              <div key={s.value} style={{ padding: "1.25rem 1.75rem", border: "1px solid rgba(201,168,76,0.3)", borderRadius: 6, backgroundColor: "rgba(201,168,76,0.06)", minWidth: 140 }}>
                <div style={{ fontFamily: "'Playfair Display', serif", fontSize: "2rem", fontWeight: 700, color: "#c9a84c", lineHeight: 1 }}>{s.value}</div>
                <div style={{ fontSize: "0.72rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "#a09070", marginTop: "0.4rem" }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECTION II: HOW IT WORKS ── */}
      <section id="service" style={{ padding: "6rem 1.5rem", backgroundColor: "#0f2030" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "4rem" }}>
            <div style={{ fontSize: "0.68rem", letterSpacing: "0.22em", textTransform: "uppercase", color: "#c9a84c", marginBottom: "0.75rem" }}>II</div>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(1.8rem, 3vw, 2.8rem)", fontWeight: 700, color: "#f5f0e8" }}>
              Designed for the Way<br />Funeral Homes Actually Work
            </h2>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "2rem" }}>
            {[
              { num: "I", title: "Zero Production Overhead", body: "You present the service. Your family submits their media. We handle every aspect of production — transcription, editing, rendering, and delivery. Your staff is never involved in the technical process." },
              { num: "II", title: "Human Review on Every Order", body: "Every memorial is personally reviewed before final render. This is not a vending machine. A trained director reviews the submission, the music, and the tone before a single frame is rendered." },
              { num: "III", title: "Delivery Within 24–48 Hours", body: "Families receive a secure, private link to their completed memorial within 24 to 48 hours of submission. Suitable for use at the service, at the reception, and for permanent family archiving." },
              { num: "IV", title: "Absolute Data Sovereignty", body: "All media is processed on private, air-gapped infrastructure. No cloud AI services. No third-party access. Family photographs and voice recordings never leave our controlled environment." },
            ].map(p => (
              <div key={p.num}
                style={{ padding: "2rem", border: "1px solid rgba(201,168,76,0.15)", borderRadius: 8, backgroundColor: "rgba(13,27,42,0.6)", transition: "border-color 0.2s" }}
                onMouseEnter={e => (e.currentTarget.style.borderColor = "rgba(201,168,76,0.4)")}
                onMouseLeave={e => (e.currentTarget.style.borderColor = "rgba(201,168,76,0.15)")}>
                <div style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.5rem", color: "#c9a84c", marginBottom: "1rem", opacity: 0.7 }}>{p.num}</div>
                <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.15rem", fontWeight: 700, color: "#f5f0e8", marginBottom: "0.75rem" }}>{p.title}</h3>
                <p style={{ fontSize: "0.9rem", lineHeight: 1.7, color: "#a09070", margin: 0 }}>{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECTION III: PARTNERSHIP TRUST ── */}
      <section id="trust" style={{ padding: "6rem 1.5rem", backgroundColor: "#0d1b2a" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "4rem", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: "0.68rem", letterSpacing: "0.22em", textTransform: "uppercase", color: "#c9a84c", marginBottom: "0.75rem" }}>III</div>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(1.8rem, 3vw, 2.6rem)", fontWeight: 700, color: "#f5f0e8", marginBottom: "1.5rem" }}>
              A Partnership Built on<br />Mutual Trust
            </h2>
            <p style={{ fontSize: "0.95rem", lineHeight: 1.8, color: "#a09070", marginBottom: "1.5rem" }}>
              Families in grief do not need to be sold to. They need to be served. When a funeral director presents the Cherished Legacy service, they are offering something genuinely valuable — a permanent, cinematic record of a life that mattered.
            </p>
            <p style={{ fontSize: "0.95rem", lineHeight: 1.8, color: "#a09070", marginBottom: "2rem" }}>
              Our wholesale pricing model means your home retains the client relationship and the margin. We remain invisible — a production partner, not a competitor.
            </p>
            <blockquote style={{ borderLeft: "3px solid #c9a84c", paddingLeft: "1.25rem", margin: 0 }}>
              <p style={{ fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontSize: "1rem", color: "#d4c9b0", lineHeight: 1.7, marginBottom: "0.5rem" }}>
                "Our team will personally review every submission within 2 business hours and begin production. The completed video is delivered within 24–48 hours of approval."
              </p>
              <cite style={{ fontSize: "0.72rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "#c9a84c" }}>— Our Service Commitment to Every Partner</cite>
            </blockquote>
          </div>
          <div style={{ borderRadius: 8, overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.5)" }}>
            <img src={PARTNERSHIP_PHOTO} alt="Funeral home director reviewing memorial partnership materials" style={{ width: "100%", height: "auto", display: "block" }} />
          </div>
        </div>
      </section>

      {/* ── SECTION IV: PRICING ── */}
      <section id="pricing" style={{ padding: "6rem 1.5rem", backgroundColor: "#0f2030" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "1rem" }}>
            <div style={{ fontSize: "0.68rem", letterSpacing: "0.22em", textTransform: "uppercase", color: "#c9a84c", marginBottom: "0.75rem" }}>IV</div>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(1.8rem, 3vw, 2.8rem)", fontWeight: 700, color: "#f5f0e8", marginBottom: "1rem" }}>Service Tiers & Wholesale Pricing</h2>
            <p style={{ color: "#a09070", fontSize: "0.9rem", maxWidth: 560, margin: "0 auto 3rem" }}>All pricing below reflects your wholesale cost. Your home sets the retail price. Suggested retail is listed for reference only.</p>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "1.5rem" }}>
            {[
              {
                num: "I", name: "The Slideshow", duration: "Up to 3 minutes",
                cost: "$35", retail: "$49", margin: "$14",
                features: ["Up to 50 photographs", "1 music selection", "Title card & closing card", "1080p digital delivery"],
                note: "Ideal for graveside services and same-day arrangements.", featured: false,
              },
              {
                num: "II", name: "The Tribute", duration: "Up to 6 minutes",
                cost: "$99", retail: "$149", margin: "$50",
                features: ["Up to 150 photographs", "3 music selections", "Voice tribute narration", "Ken Burns motion on photos", "1080p digital delivery"],
                note: "The most requested tier. Suitable for all service types.", featured: true,
              },
              {
                num: "III", name: "The Legacy", duration: "Up to 10 minutes",
                cost: "$175", retail: "$249", margin: "$74",
                features: ["Up to 300 photographs", "Unlimited music & voice tributes", "Full cinematic audio mix", "Archival-quality 1080p master", "Secure 30-day family archive link"],
                note: "For families who want a complete life narrative.", featured: false,
              },
            ].map(tier => (
              <div key={tier.num} style={{
                padding: "2rem", borderRadius: 8,
                border: tier.featured ? "2px solid #c9a84c" : "1px solid rgba(201,168,76,0.2)",
                backgroundColor: tier.featured ? "rgba(201,168,76,0.07)" : "rgba(13,27,42,0.6)",
                position: "relative",
              }}>
                {tier.featured && (
                  <div style={{ position: "absolute", top: -14, left: "50%", transform: "translateX(-50%)", backgroundColor: "#c9a84c", color: "#0d1b2a", fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", padding: "0.3rem 1rem", borderRadius: 2, whiteSpace: "nowrap" }}>
                    Most Requested
                  </div>
                )}
                <div style={{ fontSize: "0.62rem", letterSpacing: "0.15em", textTransform: "uppercase", color: "#c9a84c", marginBottom: "0.5rem" }}>{tier.num}</div>
                <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.4rem", fontWeight: 700, color: "#f5f0e8", marginBottom: "0.25rem" }}>{tier.name}</h3>
                <div style={{ fontSize: "0.8rem", color: "#a09070", marginBottom: "1.5rem" }}>{tier.duration}</div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0.5rem", marginBottom: "1.5rem", padding: "1rem", backgroundColor: "rgba(0,0,0,0.2)", borderRadius: 6 }}>
                  {[
                    { label: "Your Cost", value: tier.cost },
                    { label: "Suggested Retail", value: tier.retail },
                    { label: "Your Margin", value: tier.margin },
                  ].map(p => (
                    <div key={p.label} style={{ textAlign: "center" }}>
                      <div style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.3rem", fontWeight: 700, color: "#c9a84c" }}>{p.value}</div>
                      <div style={{ fontSize: "0.58rem", letterSpacing: "0.08em", textTransform: "uppercase", color: "#7a6a50", marginTop: 2 }}>{p.label}</div>
                    </div>
                  ))}
                </div>

                <ul style={{ listStyle: "none", padding: 0, margin: "0 0 1.5rem", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                  {tier.features.map(f => (
                    <li key={f} style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem", fontSize: "0.85rem", color: "#c4b89a" }}>
                      <span style={{ color: "#c9a84c", marginTop: 2, flexShrink: 0 }}>◆</span>
                      {f}
                    </li>
                  ))}
                </ul>
                <p style={{ fontSize: "0.8rem", color: "#7a6a50", fontStyle: "italic", margin: 0 }}>{tier.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECTION V: WORKFLOW ── */}
      <section id="workflow" style={{ padding: "6rem 1.5rem", backgroundColor: "#0d1b2a" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "4rem" }}>
            <div style={{ fontSize: "0.68rem", letterSpacing: "0.22em", textTransform: "uppercase", color: "#c9a84c", marginBottom: "0.75rem" }}>V</div>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(1.8rem, 3vw, 2.8rem)", fontWeight: 700, color: "#f5f0e8" }}>The Workflow</h2>
          </div>

          <div style={{ display: "flex", flexDirection: "column" }}>
            {[
              { num: "01", title: "Family Submits", body: "Your staff provides the family with a private intake link. They upload photos, videos, and audio at their own pace." },
              { num: "02", title: "We Review", body: "Our team reviews every submission within 2 business hours. We confirm content quality and contact the family if anything is missing." },
              { num: "03", title: "Production", body: "Our local pipeline renders the memorial — photo motion, audio mixing, narration, title cards — on private infrastructure." },
              { num: "04", title: "Director Approval", body: "A draft is reviewed by our director before final render. Nothing ships without human sign-off." },
              { num: "05", title: "Delivery", body: "The family receives a secure, private link to their completed memorial within 24–48 hours. Yours to present at the service." },
            ].map((step, i) => (
              <div key={step.num} style={{ display: "flex", gap: "2rem", alignItems: "flex-start", padding: "2rem 0", borderBottom: i < 4 ? "1px solid rgba(201,168,76,0.1)" : "none" }}>
                <div style={{ flexShrink: 0, width: 56, height: 56, borderRadius: "50%", border: "2px solid rgba(201,168,76,0.4)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <span style={{ fontFamily: "'Playfair Display', serif", fontSize: "1rem", fontWeight: 700, color: "#c9a84c" }}>{step.num}</span>
                </div>
                <div>
                  <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.2rem", fontWeight: 700, color: "#f5f0e8", marginBottom: "0.5rem" }}>{step.title}</h3>
                  <p style={{ fontSize: "0.9rem", lineHeight: 1.7, color: "#a09070", margin: 0 }}>{step.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECTION VI: INQUIRY FORM ── */}
      <section id="inquiry" style={{ padding: "6rem 1.5rem", backgroundColor: "#0f2030" }}>
        <div style={{ maxWidth: 1000, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "4rem", alignItems: "start" }}>
          <div>
            <div style={{ fontSize: "0.68rem", letterSpacing: "0.22em", textTransform: "uppercase", color: "#c9a84c", marginBottom: "0.75rem" }}>VI</div>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(1.8rem, 3vw, 2.4rem)", fontWeight: 700, color: "#f5f0e8", marginBottom: "1.5rem" }}>
              Begin a<br />Partnership Inquiry
            </h2>
            <p style={{ fontSize: "0.9rem", lineHeight: 1.8, color: "#a09070", marginBottom: "2rem" }}>
              We work with a limited number of funeral home partners in each region to ensure production quality and delivery guarantees are maintained. If you are interested in offering the Cherished Legacy service to your families, complete the inquiry form and a member of our team will respond within one business day.
            </p>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "0.6rem" }}>
              {["No setup fees or contracts", "Wholesale pricing on all tiers", "You set your own retail price", "Your brand, your relationship"].map(b => (
                <li key={b} style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.85rem", color: "#c4b89a" }}>
                  <span style={{ color: "#c9a84c", flexShrink: 0 }}>◆</span> {b}
                </li>
              ))}
            </ul>
          </div>

          <div>
            {submitted ? (
              <div style={{ padding: "2.5rem", border: "1px solid rgba(201,168,76,0.4)", borderRadius: 8, textAlign: "center" }}>
                <img src={SEAL_LOGO} alt="Seal" style={{ width: 64, height: 64, margin: "0 auto 1.5rem", display: "block", borderRadius: "50%", objectFit: "cover" }} />
                <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.4rem", color: "#c9a84c", marginBottom: "0.75rem" }}>Inquiry Received</h3>
                <p style={{ color: "#a09070", fontSize: "0.9rem", lineHeight: 1.7, margin: 0 }}>Thank you. A member of our team will respond within one business day.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                <input id="b2b-name" name="contact_name" type="text" autoComplete="name" placeholder="Your Name *" required value={form.name}
                  onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  style={inputStyle}
                  onFocus={e => (e.currentTarget.style.borderColor = "#c9a84c")}
                  onBlur={e => (e.currentTarget.style.borderColor = "rgba(201,168,76,0.25)")} />
                <input id="b2b-funeral-home" name="funeral_home" type="text" autoComplete="organization" placeholder="Funeral Home Name *" required value={form.funeralHome}
                  onChange={e => setForm(p => ({ ...p, funeralHome: e.target.value }))}
                  style={inputStyle}
                  onFocus={e => (e.currentTarget.style.borderColor = "#c9a84c")}
                  onBlur={e => (e.currentTarget.style.borderColor = "rgba(201,168,76,0.25)")} />
                <input id="b2b-email" name="contact_email" type="email" autoComplete="email" placeholder="Email Address *" required value={form.email}
                  onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                  style={inputStyle}
                  onFocus={e => (e.currentTarget.style.borderColor = "#c9a84c")}
                  onBlur={e => (e.currentTarget.style.borderColor = "rgba(201,168,76,0.25)")} />
                <input id="b2b-phone" name="contact_phone" type="tel" autoComplete="tel" placeholder="Phone Number" value={form.phone}
                  onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                  style={inputStyle}
                  onFocus={e => (e.currentTarget.style.borderColor = "#c9a84c")}
                  onBlur={e => (e.currentTarget.style.borderColor = "rgba(201,168,76,0.25)")} />
                <textarea id="b2b-message" name="inquiry_message" placeholder="Message (Optional)" rows={4} value={form.message}
                  onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
                  style={{ ...inputStyle, resize: "vertical" }}
                  onFocus={e => (e.currentTarget.style.borderColor = "#c9a84c")}
                  onBlur={e => (e.currentTarget.style.borderColor = "rgba(201,168,76,0.25)")} />
                <button type="submit" disabled={notifyMutation.isPending}
                  style={{ padding: "0.9rem 2rem", backgroundColor: "#c9a84c", color: "#0d1b2a", border: "none", borderRadius: 4, fontWeight: 700, fontSize: "0.9rem", letterSpacing: "0.05em", cursor: notifyMutation.isPending ? "not-allowed" : "pointer", opacity: notifyMutation.isPending ? 0.7 : 1, transition: "opacity 0.2s", fontFamily: "'DM Sans', sans-serif" }}>
                  {notifyMutation.isPending ? "Submitting…" : "Submit Partnership Inquiry"}
                </button>
                <p style={{ fontSize: "0.75rem", color: "#7a6a50", textAlign: "center", margin: 0 }}>Your information is held in strict confidence and will never be shared with third parties.</p>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{ padding: "3rem 1.5rem", backgroundColor: "#080f18", borderTop: "1px solid rgba(201,168,76,0.15)" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", flexDirection: "column", alignItems: "center", gap: "1rem", textAlign: "center" }}>
          <img src={SEAL_LOGO} alt="Cherished Legacy Seal" style={{ width: 56, height: 56, borderRadius: "50%", opacity: 0.85, objectFit: "cover" }} />
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.1rem", color: "#c9a84c" }}>CherishedLegacy.org</div>
          <div style={{ fontSize: "0.72rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "#7a6a50" }}>Memorial Registry · Est. 2026</div>
          <p style={{ fontSize: "0.8rem", color: "#7a6a50", maxWidth: 480, margin: 0 }}>A service for funeral professionals. All media processed on private, sovereign infrastructure.</p>
          <p style={{ fontSize: "0.72rem", color: "#5a4a30", margin: 0 }}>© 2026 CherishedLegacy.org · All rights reserved</p>
        </div>
      </footer>
    </div>
  );
}
